import React, { useMemo, useRef, useState } from "react";
import ScrollHintRail from "global/components/ScrollHintRail/ScrollHintRail";
import {
  Box,
  ButtonBase,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { LockOutlined } from "@mui/icons-material";
import { useHistory, useLocation } from "react-router-dom";
import { useSetPageHeader } from "global/components/PageHeader/PageHeaderContext";
import AnalyticsViewToggle from "../components/AnalyticsViewToggle";
import { useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import strings from "global/constants/StringConstants";
import { hasAccessTo } from "layouts/Config";
import Unauthorized from "screens/Shared/Unauthorized";
import UserRequestAnalyticsReport from "./UserRequestAnalyticsReport";
import TemplatePerformanceReport from "./TemplatePerformanceReport";
import RecipientInsightsReport from "./RecipientInsightsReport";
import RequestsReport from "./RequestsReport";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import reportsStyles from "../Reports.styles";
import { tourAttr } from "tour";
import FeatureGateBlock from "global/components/FeatureGateBlock/FeatureGateBlock";

const styles = reportsStyles;

type ReportTabKey =
  | "user-requests"
  | "template-performance"
  | "requests-overview"
  | "recipient-insights";

/**
 * `shortLabel` is what the phone and tablet reference prints — the chip rail
 * and the tablet's tighter underline row have no room for the desktop's
 * two-word labels, so each tab carries both and the breakpoint picks one.
 */
const REPORT_TABS: Array<{
  label: string;
  shortLabel: string;
  value: ReportTabKey;
}> = [
  { label: "User analytics", shortLabel: "Users", value: "user-requests" },
  {
    label: "Template analytics",
    shortLabel: "Templates",
    value: "template-performance",
  },
  { label: "Sent requests", shortLabel: "Sent", value: "requests-overview" },
  {
    label: "Recipient analytics",
    shortLabel: "Recipients",
    value: "recipient-insights",
  },
];

const PAGE_TITLE_ID = "reports-page-title";
const tabId = (value: ReportTabKey) => `reports-tab-${value}`;
const panelId = (value: ReportTabKey) => `reports-tabpanel-${value}`;

const ReportsTabsPage = () => {
  useTitle(title.ANALYTICS);
  const reportingState = useFeatureState(FeatureKeys.REPORTING_ANALYTICS);
  // Only the "User analytics" tab has its own, narrower gate — the lock shown
  // on its tab is a preview of the locked card `UserRequestAnalyticsReport`
  // itself renders once selected.
  const usageReportsState = useFeatureState(FeatureKeys.TEAM_USAGE_REPORTS);
  const isUsageReportsLocked =
    usageReportsState.status !== strings.FEATURE_STATUS_AVAILABLE;
  const history = useHistory();
  const location = useLocation();
  const isPhone = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const isCompact = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );

  // The phone reference drops the page's own `<h1>` and carries "Reports" in
  // the app bar instead; the tablet keeps it on the page.
  useSetPageHeader(isPhone ? "Reports" : undefined);

  const query = useMemo(() => new URLSearchParams(location.search), [location]);
  const tabFromUrl = query.get("tab") as ReportTabKey | null;
  const initialTab: ReportTabKey =
    tabFromUrl && REPORT_TABS.some((tab) => tab.value === tabFromUrl)
      ? tabFromUrl
      : "user-requests";

  const [currentTab, setCurrentTab] = useState<ReportTabKey>(initialTab);
  const [loader, setLoader] = useState<boolean>(false);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const hasFetchPermission = hasAccessTo(
    strings.resources.report,
    strings.fetchPermission,
  );

  const handleTabChange = (value: ReportTabKey) => {
    // A loading report still owns the panel, so swapping tabs mid-fetch is
    // suppressed here rather than by `disabled` — a disabled tab drops out of
    // the tab order and throws keyboard focus to the body (WCAG 2.4.3).
    if (loader) return;
    setCurrentTab(value);
    history.replace({
      pathname: location.pathname,
      search: `?tab=${value}`,
    });
  };

  /**
   * Roving-focus keys required of a tablist by WCAG 2.1.1: arrows move between
   * tabs (wrapping at both ends), Home/End jump to the first/last one.
   */
  const handleTabKeyDown = (event: React.KeyboardEvent, index: number) => {
    const lastIndex = REPORT_TABS.length - 1;
    let nextIndex: number | null = null;

    switch (event.key) {
      case "ArrowRight":
        nextIndex = index === lastIndex ? 0 : index + 1;
        break;
      case "ArrowLeft":
        nextIndex = index === 0 ? lastIndex : index - 1;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = lastIndex;
        break;
      default:
        return;
    }

    event.preventDefault();
    tabRefs.current[nextIndex]?.focus();
    handleTabChange(REPORT_TABS[nextIndex].value);
  };

  if (!hasFetchPermission) {
    return <Unauthorized permissionDenied={!hasFetchPermission} />;
  }

  if (reportingState.status !== strings.FEATURE_STATUS_AVAILABLE) {
    return (
      <CenteredAuthenticatedLayout>
        <Box sx={styles.page}>
          <FeatureGateBlock
            variant="full"
            featureName="Reports"
            status={reportingState.status as "not-in-plan" | "admin-disabled"}
            disabledMessage={(reportingState as any).disabledMessage}
          />
        </Box>
      </CenteredAuthenticatedLayout>
    );
  }

  return (
    <CenteredAuthenticatedLayout>
      <Box sx={styles.page}>
        <Box sx={styles.header}>
          <Box sx={styles.headerText}>
            <Typography component="h1" id={PAGE_TITLE_ID} sx={styles.title}>
              Reports
            </Typography>
            <Typography component="p" sx={styles.subtitle}>
              Detailed usage and completion analytics across users and
              recipients.
            </Typography>
          </Box>
          {/* Inert from `md` up — the desktop reference has no such switch. */}
          <AnalyticsViewToggle current="reports" />
        </Box>

        {/*
          The `<h1>` it is named by is `display: none` on a phone, where the app
          bar carries the screen name instead. A hidden element referenced by
          `aria-labelledby` still contributes its text, so the tablist keeps its
          name at every width (WCAG 4.1.2).
        */}
        <ScrollHintRail
          sx={styles.tabBarRail}
          railSx={styles.tabBar}
          railProps={{
            role: "tablist",
            "aria-labelledby": PAGE_TITLE_ID,
            ...tourAttr("reports.tabs"),
          }}
          previousLabel="Show previous reports"
          nextLabel="Show more reports"
        >
          {REPORT_TABS.map((tab, index) => {
            const isActive = currentTab === tab.value;
            return (
              <ButtonBase
                key={tab.value}
                ref={(node: HTMLButtonElement | null) => {
                  tabRefs.current[index] = node;
                }}
                disableRipple
                role="tab"
                id={tabId(tab.value)}
                aria-selected={isActive}
                aria-controls={panelId(tab.value)}
                aria-disabled={loader || undefined}
                // Only the selected tab is a tab stop; the arrow keys handled
                // above move focus within the tablist (WCAG 2.4.3).
                tabIndex={isActive ? 0 : -1}
                onClick={() => handleTabChange(tab.value)}
                onKeyDown={(event) => handleTabKeyDown(event, index)}
                sx={styles.tab(isActive)}
              >
                <Box
                  component="span"
                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                >
                  {isCompact ? tab.shortLabel : tab.label}
                  {tab.value === "user-requests" && isUsageReportsLocked && (
                    <LockOutlined
                      sx={{ fontSize: 14, opacity: 0.65, flexShrink: 0 }}
                    />
                  )}
                </Box>
              </ButtonBase>
            );
          })}
        </ScrollHintRail>

        <Box
          role="tabpanel"
          id={panelId(currentTab)}
          aria-labelledby={tabId(currentTab)}
          {...tourAttr("reports.content")}
        >
          {currentTab === "requests-overview" && (
            <RequestsReport setLoader={setLoader} />
          )}
          {currentTab === "user-requests" && (
            <UserRequestAnalyticsReport setLoader={setLoader} />
          )}
          {currentTab === "template-performance" && (
            <TemplatePerformanceReport setLoader={setLoader} />
          )}
          {currentTab === "recipient-insights" && (
            <RecipientInsightsReport setLoader={setLoader} />
          )}
        </Box>
      </Box>
    </CenteredAuthenticatedLayout>
  );
};

export default ReportsTabsPage;
